import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { saRequest } from '../../../utils/superAdminAxiosCaller';
import { setSuperAdminToken } from '../../../utils/superAdminToken';
import '../styles/super-admin.scss';

type Stage =
    | { kind: 'login' }
    | { kind: 'totp'; challenge_token: string; email: string }
    | {
          kind: 'enroll';
          setup_token: string;
          secret: string;
          otpauth_uri: string;
          email: string;
      }
    | { kind: 'recovery-codes'; codes: string[] };

interface LoginResponseChallenge {
    step: 'totp_required';
    challenge_token: string;
}

interface LoginResponseEnroll {
    step: 'enroll_totp';
    setup_token: string;
    secret: string;
    otpauth_uri: string;
}

// Direct-session response: backend can return the issued session directly
// (no TOTP step) when running on APP_ENV=local with the dev-bypass active.
// Production responses always return one of the two `step` variants.
interface LoginResponseDirectSession {
    access_token: string;
    token_type?: string;
    expires_at?: string;
    super_admin?: { id: number; name: string; email: string };
    new_ip?: boolean;
}

type LoginResponse = LoginResponseChallenge | LoginResponseEnroll | LoginResponseDirectSession;

interface SessionResponse {
    access_token: string;
    token_type: string;
    expires_at: string;
    super_admin: { id: number; name: string; email: string };
    new_ip?: boolean;
    recovery_codes?: string[];
}

function parseAxiosError(err: any): { banner: string | null; fields: Record<string, string> } {
    const status = err?.response?.status;
    const data = err?.response?.data;

    if (status === 422 && data?.errors && typeof data.errors === 'object') {
        const fields: Record<string, string> = {};
        for (const k in data.errors) {
            if (Object.prototype.hasOwnProperty.call(data.errors, k)) {
                const v = data.errors[k];
                fields[k] = Array.isArray(v) ? String(v[0]) : String(v);
            }
        }
        return { banner: null, fields };
    }

    if (status === 429) {
        return {
            banner: 'Too many attempts. Please wait a few minutes and try again.',
            fields: {},
        };
    }

    const msg = data?.message || data?.error?.params?.message;
    if (status === 401) {
        return { banner: msg || 'Authentication failed.', fields: {} };
    }

    return { banner: msg || 'Something went wrong. Please try again.', fields: {} };
}

const LoginPage: React.FC = () => {
    const history = useHistory();
    const [stage, setStage] = useState<Stage>({ kind: 'login' });
    const [submitting, setSubmitting] = useState(false);
    const [banner, setBanner] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    // Controlled inputs (kept at top so they reset cleanly when stage changes).
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [code, setCode] = useState('');
    const [savedConfirm, setSavedConfirm] = useState(false);

    const codeInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        // Auto-focus code field when we enter totp/enroll stages.
        if ((stage.kind === 'totp' || stage.kind === 'enroll') && codeInputRef.current) {
            codeInputRef.current.focus();
        }
    }, [stage.kind]);

    const resetToLogin = () => {
        setStage({ kind: 'login' });
        setCode('');
        setPassword('');
        setBanner(null);
        setFieldErrors({});
        setSavedConfirm(false);
    };

    const handleLoginSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        setBanner(null);
        setFieldErrors({});

        try {
            const res = await saRequest<LoginResponse>('POST', 'auth/login', {
                email,
                password,
            });

            const data: any = res.data;
            if (data.step === 'totp_required') {
                setStage({
                    kind: 'totp',
                    challenge_token: data.challenge_token,
                    email,
                });
                setCode('');
            } else if (data.step === 'enroll_totp') {
                setStage({
                    kind: 'enroll',
                    setup_token: data.setup_token,
                    secret: data.secret,
                    otpauth_uri: data.otpauth_uri,
                    email,
                });
                setCode('');
            } else if (typeof data.access_token === 'string') {
                // Direct session (TOTP bypass on local). Persist the token
                // and head straight to the dashboard, mirroring the
                // post-TOTP success path.
                setSuperAdminToken(data.access_token);
                history.replace('/mika/ops');
            } else {
                setBanner('Unexpected server response. Please retry.');
            }
        } catch (err) {
            const p = parseAxiosError(err);
            setBanner(p.banner);
            setFieldErrors(p.fields);
        } finally {
            setSubmitting(false);
        }
    };

    const handleTotpSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (submitting || stage.kind !== 'totp') return;
        setSubmitting(true);
        setBanner(null);
        setFieldErrors({});

        try {
            const res = await saRequest<SessionResponse>('POST', 'auth/totp', {
                challenge_token: stage.challenge_token,
                code: code.trim(),
            });

            setSuperAdminToken(res.data.access_token);
            history.replace('/mika/ops');
        } catch (err) {
            const p = parseAxiosError(err);
            setBanner(p.banner);
            setFieldErrors(p.fields);
            if (err?.response?.status === 401) {
                // Challenge token expired or invalid — bounce back to login.
                setTimeout(resetToLogin, 1800);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleEnrollSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (submitting || stage.kind !== 'enroll') return;
        setSubmitting(true);
        setBanner(null);
        setFieldErrors({});

        try {
            const res = await saRequest<SessionResponse>('POST', 'auth/totp/enroll', {
                setup_token: stage.setup_token,
                code: code.trim(),
            });

            setSuperAdminToken(res.data.access_token);
            const codes = res.data.recovery_codes || [];
            setStage({ kind: 'recovery-codes', codes });
            setCode('');
        } catch (err) {
            const p = parseAxiosError(err);
            setBanner(p.banner);
            setFieldErrors(p.fields);
            if (err?.response?.status === 401) {
                // Setup token expired or invalid — back to login.
                setTimeout(resetToLogin, 1800);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const copyCodesToClipboard = async (codes: string[]) => {
        try {
            await navigator.clipboard.writeText(codes.join('\n'));
            setBanner('Recovery codes copied to clipboard.');
            setTimeout(() => setBanner(null), 2000);
        } catch (e) {
            setBanner('Clipboard unavailable — please copy manually.');
        }
    };

    const downloadCodesTxt = (codes: string[]) => {
        const content = [
            'Mika Ops — TOTP recovery codes',
            'Each code is single-use. Store these somewhere safe (password manager, printed, etc.).',
            '',
            ...codes,
            '',
            `Generated: ${new Date().toISOString()}`,
        ].join('\n');
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'mika-ops-recovery-codes.txt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // ----- Render --------------------------------------------------------

    return (
        <div className="sa-shell">
            <main className="sa-content" style={{ maxWidth: 480, marginTop: 64 }}>
                <div className="sa-brand" style={{ justifyContent: 'center', marginBottom: 32 }}>
                    <span className="sa-brand-dot" aria-hidden="true" />
                    <span className="sa-brand-mark" style={{ fontSize: 18, fontWeight: 700 }}>Mika</span>
                    <span className="sa-brand-suffix" style={{ fontSize: 18, fontWeight: 700 }}>Ops</span>
                </div>

                <div className="sa-card">
                    {banner && (
                        <div
                            style={{
                                marginBottom: 16,
                                padding: '10px 12px',
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                color: '#fca5a5',
                                borderRadius: 6,
                                fontSize: 13,
                            }}
                        >
                            {banner}
                        </div>
                    )}

                    {stage.kind === 'login' && (
                        <form onSubmit={handleLoginSubmit} noValidate>
                            <h1 style={{ fontSize: 20, marginBottom: 4 }}>Sign in</h1>
                            <p style={{ color: 'var(--sa-text-muted)', fontSize: 13, marginBottom: 24 }}>
                                Internal tooling — authorized staff only.
                            </p>

                            <div className="sa-field">
                                <label htmlFor="sa-email">Email</label>
                                <input
                                    id="sa-email"
                                    type="email"
                                    autoComplete="username"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    disabled={submitting}
                                    required
                                />
                                {fieldErrors.email && <div className="sa-field-error">{fieldErrors.email}</div>}
                            </div>

                            <div className="sa-field">
                                <label htmlFor="sa-password">Password</label>
                                <input
                                    id="sa-password"
                                    type="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    disabled={submitting}
                                    required
                                />
                                {fieldErrors.password && <div className="sa-field-error">{fieldErrors.password}</div>}
                            </div>

                            <button
                                type="submit"
                                className="sa-btn sa-btn-primary"
                                style={{ width: '100%', marginTop: 8 }}
                                disabled={submitting || !email || !password}
                            >
                                {submitting && <span className="sa-btn-spinner" />}
                                {submitting ? 'Signing in…' : 'Continue'}
                            </button>
                        </form>
                    )}

                    {stage.kind === 'totp' && (
                        <form onSubmit={handleTotpSubmit} noValidate>
                            <h1 style={{ fontSize: 20, marginBottom: 4 }}>Two-factor code</h1>
                            <p style={{ color: 'var(--sa-text-muted)', fontSize: 13, marginBottom: 24 }}>
                                Enter the 6-digit code from your authenticator app, or a 10-character recovery code if you've lost your device.
                            </p>

                            <div className="sa-field">
                                <label htmlFor="sa-code">Code</label>
                                <input
                                    id="sa-code"
                                    ref={codeInputRef}
                                    type="text"
                                    inputMode="text"
                                    autoComplete="one-time-code"
                                    spellCheck={false}
                                    autoCapitalize="off"
                                    value={code}
                                    onChange={e => setCode(e.target.value)}
                                    disabled={submitting}
                                    required
                                    style={{ fontSize: 16, letterSpacing: '0.1em' }}
                                />
                                {fieldErrors.code && <div className="sa-field-error">{fieldErrors.code}</div>}
                            </div>

                            <button
                                type="submit"
                                className="sa-btn sa-btn-primary"
                                style={{ width: '100%', marginTop: 8 }}
                                disabled={submitting || code.trim().length < 6}
                            >
                                {submitting && <span className="sa-btn-spinner" />}
                                {submitting ? 'Verifying…' : 'Verify'}
                            </button>

                            <button
                                type="button"
                                className="sa-btn sa-btn-secondary"
                                style={{ width: '100%', marginTop: 10 }}
                                onClick={resetToLogin}
                                disabled={submitting}
                            >
                                Back
                            </button>
                        </form>
                    )}

                    {stage.kind === 'enroll' && (
                        <form onSubmit={handleEnrollSubmit} noValidate>
                            <h1 style={{ fontSize: 20, marginBottom: 4 }}>Set up two-factor</h1>
                            <p style={{ color: 'var(--sa-text-muted)', fontSize: 13, marginBottom: 20 }}>
                                Scan the QR code with Google Authenticator, Authy, 1Password, or any TOTP app. Then enter the 6-digit code the app generates to confirm.
                            </p>

                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    padding: 16,
                                    background: '#fff',
                                    borderRadius: 8,
                                    marginBottom: 16,
                                }}
                            >
                                <QRCodeSVG value={stage.otpauth_uri} size={200} level="M" />
                            </div>

                            <details style={{ marginBottom: 16 }}>
                                <summary
                                    style={{
                                        cursor: 'pointer',
                                        fontSize: 12,
                                        color: 'var(--sa-text-muted)',
                                        marginBottom: 8,
                                    }}
                                >
                                    Can't scan? Enter this secret manually
                                </summary>
                                <code
                                    style={{
                                        display: 'block',
                                        padding: '8px 12px',
                                        background: 'var(--sa-surface)',
                                        border: '1px solid var(--sa-border-strong)',
                                        borderRadius: 4,
                                        fontSize: 12,
                                        wordBreak: 'break-all',
                                    }}
                                >
                                    {stage.secret}
                                </code>
                            </details>

                            <div className="sa-field">
                                <label htmlFor="sa-enroll-code">6-digit code from your app</label>
                                <input
                                    id="sa-enroll-code"
                                    ref={codeInputRef}
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    spellCheck={false}
                                    maxLength={6}
                                    value={code}
                                    onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                                    disabled={submitting}
                                    required
                                    style={{ fontSize: 18, letterSpacing: '0.2em', textAlign: 'center' }}
                                />
                                {fieldErrors.code && <div className="sa-field-error">{fieldErrors.code}</div>}
                            </div>

                            <button
                                type="submit"
                                className="sa-btn sa-btn-primary"
                                style={{ width: '100%', marginTop: 8 }}
                                disabled={submitting || code.length !== 6}
                            >
                                {submitting && <span className="sa-btn-spinner" />}
                                {submitting ? 'Confirming…' : 'Confirm & continue'}
                            </button>

                            <button
                                type="button"
                                className="sa-btn sa-btn-secondary"
                                style={{ width: '100%', marginTop: 10 }}
                                onClick={resetToLogin}
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                        </form>
                    )}

                    {stage.kind === 'recovery-codes' && (
                        <div>
                            <h1 style={{ fontSize: 20, marginBottom: 4 }}>Save these recovery codes</h1>
                            <p style={{ color: 'var(--sa-text-muted)', fontSize: 13, marginBottom: 16 }}>
                                These 10 codes are shown <strong>once</strong>. Each is single-use. Use one to log in if you lose your authenticator app. If you lose all of them AND your phone, a server-side CLI reset is required.
                            </p>

                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: 8,
                                    padding: 12,
                                    background: 'var(--sa-surface)',
                                    border: '1px solid var(--sa-border-strong)',
                                    borderRadius: 6,
                                    marginBottom: 16,
                                    fontFamily: 'monospace',
                                    fontSize: 13,
                                }}
                            >
                                {stage.codes.map((c, i) => (
                                    <div key={i} style={{ letterSpacing: '0.1em' }}>
                                        <span style={{ color: 'var(--sa-text-muted)', marginRight: 8 }}>
                                            {String(i + 1).padStart(2, '0')}.
                                        </span>
                                        {c}
                                    </div>
                                ))}
                            </div>

                            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                                <button
                                    type="button"
                                    className="sa-btn sa-btn-secondary"
                                    style={{ flex: 1 }}
                                    onClick={() => copyCodesToClipboard(stage.codes)}
                                >
                                    Copy all
                                </button>
                                <button
                                    type="button"
                                    className="sa-btn sa-btn-secondary"
                                    style={{ flex: 1 }}
                                    onClick={() => downloadCodesTxt(stage.codes)}
                                >
                                    Download .txt
                                </button>
                            </div>

                            <label
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 8,
                                    fontSize: 13,
                                    color: 'var(--sa-text-muted)',
                                    marginBottom: 16,
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={savedConfirm}
                                    onChange={e => setSavedConfirm(e.target.checked)}
                                    style={{ marginTop: 3 }}
                                />
                                <span>
                                    I have saved these codes somewhere safe. I understand they will
                                    not be shown again.
                                </span>
                            </label>

                            <button
                                type="button"
                                className="sa-btn sa-btn-primary"
                                style={{ width: '100%' }}
                                disabled={!savedConfirm}
                                onClick={() => history.replace('/mika/ops')}
                            >
                                Continue to dashboard
                            </button>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default LoginPage;
